import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { t as translate, LANGUAGES } from '@/i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('luxe_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('luxe_lang') || 'en');
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('luxe_cart') || '[]'); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem('luxe_lang', lang); document.documentElement.lang = lang; }, [lang]);
  useEffect(() => { localStorage.setItem('luxe_cart', JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    const token = localStorage.getItem('luxe_token');
    if (token) {
      apiClient.get('/auth/me').then((r) => setUser(r.data)).catch(() => {
        localStorage.removeItem('luxe_token');
      });
    }
  }, []);

  const t = useCallback((key) => translate(lang, key), [lang]);  // translate is module-scoped import (stable)

  const login = async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    localStorage.setItem('luxe_token', data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (name, email, password) => {
    const { data } = await apiClient.post('/auth/register', { name, email, password });
    localStorage.setItem('luxe_token', data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => { localStorage.removeItem('luxe_token'); setUser(null); };

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) return prev.map((i) => i.id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { id: product.id, title: product.title, designer: product.designer, price: product.price, image: (product.images || [])[0], quantity: qty }];
    });
  };
  const removeFromCart = (id) => setCart((p) => p.filter((i) => i.id !== id));
  const updateQty = (id, qty) => setCart((p) => p.map((i) => i.id === id ? { ...i, quantity: Math.max(1, qty) } : i));
  const clearCart = () => setCart([]);

  const value = {
    lang, setLang, languages: LANGUAGES, t,
    user, login, register, logout,
    cart, addToCart, removeFromCart, updateQty, clearCart,
    api: apiClient, API,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
